/**
 * The same-origin hand-off between World and Harry (ADR-040; ADR-224 §V1
 * "link only"). World and Harry are both served from poqpoq.com, so they share
 * ONE IndexedDB: World leaves the upload it refused under `world-to-harry` and
 * opens `/harry/?handoff=1` in a new tab; Harry leaves the person's fixed export
 * under `harry-to-world`. Nothing travels over the network and nothing happens
 * without a click on either side.
 *
 * THE CONTRACT (both sides create the store identically on upgrade):
 *   database `poqpoq-handoff`, version 1, object store `files`, OUT-OF-LINE keys
 *   (`createObjectStore('files')`, `put(record, key)`).
 *   world-to-harry: { v:1, name, bytes:Blob, sha256, size, createdAt:ISO,
 *                     from:'world-upload', verdict:{ reasons:string[] }, userId }
 *   harry-to-world: { v:1, name, bytes:Blob, sha256, size, createdAt:ISO,
 *                     from:'harry-export', harrySha, fixes:string[], userId }
 *   `bytes` is a Blob typed `model/gltf-binary`; `sha256` is lowercase hex of
 *   the blob's bytes.
 *
 * TRUST NOTHING BUT THE BYTES. `name`, `verdict.reasons` and every other text
 * field are DISPLAY ONLY: rendered with textContent, never branched on. Harry
 * re-runs its own check on the bytes. The only fields read for control are the
 * shape (`v`, `from`), the integrity pair (`size`, `sha256`), the age
 * (`createdAt`), and `userId`, which Harry copies, never interprets.
 *
 * FAIL LOUD. Every storage access can fail (private window, blocked storage,
 * quota): each answers with a reason the caller puts on the status line, and the
 * fallback is the plain manual drop. Nothing here swallows an error.
 *
 * Depends on: an injected HandoffBackend (the IndexedDB adapter below in the
 * browser; an in-memory map in tests) and an injected sha256. Depended on by:
 * intake/intake-session.ts.
 */

export const HANDOFF_DB = 'poqpoq-handoff';
export const HANDOFF_DB_VERSION = 1;
export const HANDOFF_STORE = 'files';
export const WORLD_TO_HARRY = 'world-to-harry';
export const HARRY_TO_WORLD = 'harry-to-world';
/** World's entry is ignored once older than this (Allen's ruling: a day). */
export const HANDOFF_MAX_AGE_MS = 24 * 60 * 60 * 1000;
export const GLB_MIME = 'model/gltf-binary';

/** The operations the hand-off needs, nothing else. */
export interface HandoffBackend {
  get(key: string): Promise<unknown>;
  put(key: string, value: unknown): Promise<void>;
  delete(key: string): Promise<void>;
  /**
   * Delete `key` only if `owned(value)` — the read and the delete in ONE readwrite transaction, so
   * another tab cannot replace the record between the check and the delete (Codex P2, #239).
   * Answers whether it deleted.
   */
  deleteIf(key: string, owned: (value: unknown) => boolean): Promise<boolean>;
}

export interface WorldHandoff {
  readonly v: 1;
  /** DISPLAY ONLY. */
  readonly name: string;
  readonly bytes: Blob;
  readonly sha256: string;
  readonly size: number;
  readonly createdAt: string;
  readonly from: 'world-upload';
  /** DISPLAY ONLY — World's words; Harry re-checks the bytes. */
  readonly verdict: { readonly reasons: readonly string[] };
  /** Copied into the export's record, never interpreted. */
  readonly userId: string | null;
  /**
   * The World tab that pressed Open in Harry (World #2280: an opaque nonce, ≤ WORLD_TAB_MAX). Copied
   * unchanged into the return record and the channel ping, never interpreted. Absent, or malformed
   * (not a string, empty, too long) = dropped, never fatal.
   */
  readonly worldTab?: string;
}

/** The longest worldTab nonce Harry carries; a longer one is dropped on read. */
export const WORLD_TAB_MAX = 64;

/** A worldTab as read: a non-empty string of ≤ WORLD_TAB_MAX characters, else nothing. */
export function readWorldTab(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 && v.length <= WORLD_TAB_MAX ? v : undefined;
}

export interface HarryHandoff {
  readonly v: 1;
  readonly name: string;
  readonly bytes: Blob;
  readonly sha256: string;
  readonly size: number;
  readonly createdAt: string;
  readonly from: 'harry-export';
  readonly harrySha: string;
  readonly fixes: readonly string[];
  /** World's user, copied from the world-to-harry record; null = a manual-drop session (World will not offer it). */
  readonly userId: string | null;
  /**
   * The Harry page session that wrote it — a random id minted once per page load. Harry's own
   * ownership mark, so a session withdraws only what it wrote (two tabs can export identical bytes,
   * so a sha cannot say who wrote it). World ignores it (an extra field is display-only there).
   */
  readonly writer: string;
  /**
   * The name World shows for it (≤ 64 characters, DISPLAY ONLY): the person's own name for the avatar when
   * they gave one, else Harry's tidied file name. Absent = World tidies the file name itself.
   */
  readonly displayName?: string;
  /** World's worldTab from the world-to-harry record, copied unchanged; absent when there was none. */
  readonly worldTab?: string;
}

/** The longest display name the record carries. */
export const DISPLAY_NAME_MAX = 64;

export type WorldHandoffRead =
  | { readonly kind: 'none' }
  | { readonly kind: 'offer'; readonly record: WorldHandoff; readonly bytes: ArrayBuffer; readonly ageMs: number }
  /** Present but not usable — stale, tampered, or not the contract's shape. Left in place: World owns its cleanup. */
  | { readonly kind: 'ignored'; readonly reason: string }
  /** Storage itself refused (private window, blocked, quota). */
  | { readonly kind: 'unavailable'; readonly reason: string };

export interface HandoffClock {
  now(): number;
  sha256(bytes: ArrayBuffer): Promise<string>;
}

const reasonOf = (err: unknown): string => (err instanceof Error ? err.message || err.name : String(err));

function isBlobLike(v: unknown): v is Blob {
  return typeof v === 'object' && v !== null && typeof (v as Blob).arrayBuffer === 'function' && typeof (v as Blob).size === 'number';
}

/** The world-to-harry shape, or the first reason it is not. Text fields are only type-checked. */
function shapeOf(raw: unknown): { record: WorldHandoff } | { reason: string } {
  if (typeof raw !== 'object' || raw === null) return { reason: 'the entry is not a record' };
  const r = raw as Record<string, unknown>;
  if (r['v'] !== 1) return { reason: `unknown version ${JSON.stringify(r['v'])}` };
  if (r['from'] !== 'world-upload') return { reason: `not from World's upload (from: ${JSON.stringify(r['from'])})` };
  if (!isBlobLike(r['bytes'])) return { reason: 'it carries no file bytes' };
  if (typeof r['sha256'] !== 'string' || !/^[0-9a-f]{64}$/.test(r['sha256'])) return { reason: 'its sha256 is missing or malformed' };
  if (typeof r['size'] !== 'number') return { reason: 'its size is missing' };
  if (typeof r['createdAt'] !== 'string' || Number.isNaN(Date.parse(r['createdAt']))) return { reason: 'its createdAt is missing or unreadable' };
  const name = typeof r['name'] === 'string' && r['name'].trim() !== '' ? r['name'] : 'avatar.glb';
  const verdict = r['verdict'] as { reasons?: unknown } | undefined;
  const reasons = Array.isArray(verdict?.reasons) ? verdict.reasons.filter((x): x is string => typeof x === 'string') : [];
  const userId = typeof r['userId'] === 'string' ? r['userId'] : null;
  const worldTab = readWorldTab(r['worldTab']);
  return {
    record: { v: 1, name, bytes: r['bytes'], sha256: r['sha256'], size: r['size'], createdAt: r['createdAt'], from: 'world-upload', verdict: { reasons }, userId, ...(worldTab !== undefined ? { worldTab } : {}) },
  };
}

/**
 * Read World's entry and verify it: shape, age (≤ 24 h, and not from the
 * future), size and sha256 of the bytes themselves. Only a verified entry is
 * offered; everything else says why.
 */
export async function readWorldHandoff(backend: HandoffBackend, clock: HandoffClock): Promise<WorldHandoffRead> {
  let raw: unknown;
  try {
    raw = await backend.get(WORLD_TO_HARRY);
  } catch (err) {
    return { kind: 'unavailable', reason: reasonOf(err) };
  }
  if (raw === undefined || raw === null) return { kind: 'none' };
  const shaped = shapeOf(raw);
  if ('reason' in shaped) return { kind: 'ignored', reason: shaped.reason };
  const record = shaped.record;
  const ageMs = clock.now() - Date.parse(record.createdAt);
  if (ageMs > HANDOFF_MAX_AGE_MS) return { kind: 'ignored', reason: `it is ${Math.floor(ageMs / 3_600_000)} h old — World's hand-off lasts 24 h; upload it in World again` };
  if (ageMs < -5 * 60 * 1000) return { kind: 'ignored', reason: 'its createdAt is in the future' };
  let bytes: ArrayBuffer;
  try {
    bytes = await record.bytes.arrayBuffer();
  } catch (err) {
    return { kind: 'unavailable', reason: `the file could not be read back (${reasonOf(err)})` };
  }
  if (bytes.byteLength !== record.size) return { kind: 'ignored', reason: `its size does not match (${bytes.byteLength} bytes, record says ${record.size})` };
  const sha = await clock.sha256(bytes);
  if (sha !== record.sha256) return { kind: 'ignored', reason: 'its sha256 does not match the bytes' };
  return { kind: 'offer', record, bytes, ageMs };
}

/** Remove World's entry once Harry has taken it. Throws with the storage's reason. */
export async function consumeWorldHandoff(backend: HandoffBackend): Promise<void> {
  await backend.delete(WORLD_TO_HARRY);
}

export interface HarryHandoffInput {
  readonly name: string;
  readonly bytes: ArrayBuffer;
  readonly harrySha: string;
  readonly fixes: readonly string[];
  readonly userId: string | null;
  /** This page session's writer id (`mintWriterId`). */
  readonly writer: string;
  /** The name World shows (trimmed to DISPLAY_NAME_MAX); omitted when empty. */
  readonly displayName?: string;
  /** World's worldTab, copied unchanged; omitted when absent or malformed. */
  readonly worldTab?: string;
}

/** Write the export World offers back to the person. Throws with the storage's reason (quota, blocked). */
export async function writeHarryHandoff(backend: HandoffBackend, input: HarryHandoffInput, clock: HandoffClock): Promise<HarryHandoff> {
  const record: HarryHandoff = {
    v: 1,
    name: input.name,
    bytes: new Blob([input.bytes], { type: GLB_MIME }),
    sha256: await clock.sha256(input.bytes),
    size: input.bytes.byteLength,
    createdAt: new Date(clock.now()).toISOString(),
    from: 'harry-export',
    harrySha: input.harrySha,
    fixes: [...input.fixes],
    userId: input.userId,
    writer: input.writer,
    ...(input.displayName !== undefined && input.displayName.trim() !== '' ? { displayName: input.displayName.trim().slice(0, DISPLAY_NAME_MAX) } : {}),
    ...(readWorldTab(input.worldTab) !== undefined ? { worldTab: input.worldTab } : {}),
  };
  await backend.put(HARRY_TO_WORLD, record);
  return record;
}

/**
 * Withdraw Harry's return record — but ONLY one this page session wrote: its `writer` must be this
 * session's id AND its sha256 one this session wrote. A record another tab or an earlier visit left
 * is that export's own promise to World and is not this session's to take back. The check and the
 * delete are one transaction (`deleteIf`). Answers whether a record was withdrawn; throws with the
 * storage's reason.
 */
export async function withdrawHarryHandoff(backend: HandoffBackend, owner: { readonly writer: string; readonly shas: ReadonlySet<string> }): Promise<boolean> {
  return backend.deleteIf(HARRY_TO_WORLD, (raw) => {
    if (typeof raw !== 'object' || raw === null) return false;
    const r = raw as { writer?: unknown; sha256?: unknown };
    return r.writer === owner.writer && typeof r.sha256 === 'string' && owner.shas.has(r.sha256);
  });
}

/** A writer id for this page session: 128 random bits, hex (getRandomValues works outside secure contexts too). */
export function mintWriterId(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * The browser backend over IndexedDB. Each call opens, does one transaction,
 * and closes, so a World tab holding the database never blocks an upgrade for
 * long. `factory` is injected as a getter (`() => globalThis.indexedDB`): reading
 * it can itself throw (a sandboxed frame), and that reason must reach the line.
 */
export function indexedDbBackend(factoryOf: () => IDBFactory | undefined): HandoffBackend {
  const open = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
      const factory = factoryOf();
      if (factory === undefined) {
        reject(new Error('this browser offers no IndexedDB'));
        return;
      }
      const req = factory.open(HANDOFF_DB, HANDOFF_DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(HANDOFF_STORE)) db.createObjectStore(HANDOFF_STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('IndexedDB refused to open'));
      req.onblocked = () => reject(new Error('IndexedDB is blocked by another tab'));
    });
  const run = async <T>(mode: IDBTransactionMode, op: (store: IDBObjectStore) => IDBRequest): Promise<T> => {
    const db = await open();
    try {
      return await new Promise<T>((resolve, reject) => {
        const tx = db.transaction(HANDOFF_STORE, mode);
        const req = op(tx.objectStore(HANDOFF_STORE));
        let value: T;
        req.onsuccess = () => {
          value = req.result as T;
        };
        tx.oncomplete = () => resolve(value);
        tx.onerror = () => reject(tx.error ?? req.error ?? new Error('IndexedDB transaction failed'));
        tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
      });
    } finally {
      db.close();
    }
  };
  return {
    get: (key) => run<unknown>('readonly', (s) => s.get(key)),
    put: async (key, value) => {
      await run<unknown>('readwrite', (s) => s.put(value, key));
    },
    delete: async (key) => {
      await run<unknown>('readwrite', (s) => s.delete(key));
    },
    deleteIf: async (key, owned) => {
      const db = await open();
      try {
        return await new Promise<boolean>((resolve, reject) => {
          const tx = db.transaction(HANDOFF_STORE, 'readwrite');
          const store = tx.objectStore(HANDOFF_STORE);
          let deleted = false;
          const read = store.get(key);
          // The delete is issued from the read's success, inside the same transaction: nothing can interleave.
          read.onsuccess = () => {
            if (owned(read.result)) {
              store.delete(key);
              deleted = true;
            }
          };
          tx.oncomplete = () => resolve(deleted);
          tx.onerror = () => reject(tx.error ?? read.error ?? new Error('IndexedDB transaction failed'));
          tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
        });
      } finally {
        db.close();
      }
    },
  };
}
